import { RollbackOutlined } from '@ant-design/icons';
import { useLocation } from '@umijs/max';
import {
  getProjectIdFromLocation,
  isProjectObjectPath,
  PROJECT_LIST_PATH,
  PROJECT_STORAGE_KEY,
} from 'config/projectObjectModel';
import React from 'react';
import { getProjectById } from '@/pages/projects/data';
import { navigateWithBrowserGestureSupport } from '@/utils/linkNavigation';

const useResolvedProjectObject = () => {
  const location = useLocation();
  const projectId = React.useMemo(
    () => getProjectIdFromLocation(location.pathname, location.search),
    [location.pathname, location.search],
  );
  const [currentProject, setCurrentProject] =
    React.useState<API.Project | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined' || !projectId) {
      return;
    }

    window.localStorage.setItem(PROJECT_STORAGE_KEY, projectId);
  }, [projectId]);

  React.useEffect(() => {
    let active = true;

    const syncCurrentProject = async () => {
      if (!projectId) {
        if (active) {
          setCurrentProject(null);
        }
        return;
      }

      const project = await getProjectById(projectId);
      if (active) {
        setCurrentProject(project ?? null);
      }
    };

    void syncCurrentProject();
    window.addEventListener('software-project-changed', syncCurrentProject);

    return () => {
      active = false;
      window.removeEventListener(
        'software-project-changed',
        syncCurrentProject,
      );
    };
  }, [projectId]);

  return {
    projectId,
    projectName:
      currentProject?.cnName ||
      currentProject?.name ||
      currentProject?.identifier ||
      projectId ||
      '项目空间',
  };
};

export const useCurrentProjectId = () => {
  const location = useLocation();
  return React.useMemo(
    () => getProjectIdFromLocation(location.pathname, location.search),
    [location.pathname, location.search],
  );
};

export const ProjectObjectHeaderBanner: React.FC = () => {
  const location = useLocation();
  const { projectId, projectName } = useResolvedProjectObject();

  if (!projectId || !isProjectObjectPath(location.pathname)) {
    return null;
  }

  return (
    <div className="app-header-detail-banner-shell">
      <fieldset className="app-header-detail-banner" aria-label="当前项目">
        <a
          href={PROJECT_LIST_PATH}
          className="app-header-detail-back-btn"
          onClick={(event) =>
            navigateWithBrowserGestureSupport(event, PROJECT_LIST_PATH)
          }
        >
          <RollbackOutlined />
          <span className="app-header-detail-back-text">返回</span>
        </a>
        <span className="app-header-detail-banner-name" title={projectName}>
          {projectName}
        </span>
      </fieldset>
    </div>
  );
};
